import { LiftOperator } from '../../liftoperator';
import {
    AggregatedTicket,
    AggregatedTicketSearchParams,
    convertAggregatedTicketSearchParamsToBackend,
} from '../../aggregatedTicket';
import {
    convertOrderTicketToFrontend,
    groupOrderTicketsByTicketName,
    OrderTicket,
    OrderTicketCollection,
} from '../../orderTicket';
import {
    mapPackageTicketToFrontend,
    mapProductToFrontend,
    PackageTicket,
    Product,
} from '../../product';
import { SalesStatistic, Environment } from '../../environment';
import {
    convertIsoDateFromDate,
    type BackendType,
    buildSearchParams,
    urlBuilder,
} from '@starjack/utils';
import { baseApi } from '@starjack/api';

type GetStatisticsParams = { environments?: Environment[] };

type ProductDetailsResponse = {
    products: OrderTicket<BackendType>[];
};

export const liftOperatorUrls = {
    liftOperator: urlBuilder('liftoperators', `liftoperators`),
    aggregatedTicket: urlBuilder('order', 'liftoperators/tickets-aggregated'),
    liftOperatorStatistics: urlBuilder('order', 'liftoperators/statistics'),
};

export const liftOperatorApi = baseApi
    .enhanceEndpoints({ addTagTypes: ['liftOperator'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getLiftOperators: builder.query<LiftOperator[], void>({
                query: () => `${liftOperatorUrls.liftOperator}?size=1000`,
                transformResponse: (response: { content: LiftOperator[] }) => {
                    return response.content;
                },
                providesTags: ['liftOperator'],
            }),
            getLiftOperator: builder.query<
                LiftOperator,
                { liftOperatorId: string }
            >({
                query: ({ liftOperatorId }) =>
                    `${liftOperatorUrls.liftOperator}/${liftOperatorId}`,
                providesTags: ['liftOperator'],
            }),
            putLiftOperator: builder.mutation<LiftOperator, LiftOperator>({
                query: (liftOperator) => ({
                    method: 'PUT',
                    url: `${liftOperatorUrls.liftOperator}/${liftOperator.id}`,
                    body: liftOperator,
                }),
                invalidatesTags: ['liftOperator'],
            }),
            getAggregatedTickets: builder.query<
                AggregatedTicket[],
                AggregatedTicketSearchParams | void
            >({
                query: (params: AggregatedTicketSearchParams = {}) =>
                    `${liftOperatorUrls.aggregatedTicket}${buildSearchParams(
                        convertAggregatedTicketSearchParamsToBackend(params)
                    )}`,
                transformResponse: (response: {
                    aggregatedTickets: AggregatedTicket[];
                }) => {
                    return response.aggregatedTickets;
                },
                providesTags: ['liftOperator'],
            }),
            getStatistics: builder.query<
                SalesStatistic,
                GetStatisticsParams | void
            >({
                query: (params: GetStatisticsParams = {}) =>
                    `${liftOperatorUrls.liftOperatorStatistics}${buildSearchParams(params)}`,
                providesTags: ['liftOperator'],
            }),
            getProductDetails: builder.query<
                OrderTicketCollection[],
                {
                    liftOperatorId: string;
                    startDay: Date;
                }
            >({
                query: ({ liftOperatorId, startDay }) => {
                    const searchParams = new URLSearchParams();

                    if (startDay) {
                        searchParams.append(
                            'startDay',
                            convertIsoDateFromDate(startDay)
                        );
                    }

                    searchParams.append('environment', 'B2B');

                    return `${
                        liftOperatorUrls.liftOperator
                    }/${liftOperatorId}/products-details?${searchParams.toString()}`;
                },
                transformResponse: (
                    requestResponse: ProductDetailsResponse
                ) => {
                    const orderTickets = requestResponse.products.map(
                        convertOrderTicketToFrontend
                    );

                    return groupOrderTicketsByTicketName(orderTickets);
                },
                providesTags: ['liftOperator'],
            }),
            getLiftOperatorProducts: builder.query<
                Product[],
                { liftOperatorId: string }
            >({
                query: ({ liftOperatorId }) =>
                    urlBuilder(
                        'liftoperators',
                        `liftoperators/${liftOperatorId}/products`
                    ),
                transformResponse: (response: {
                    tickets: Product<BackendType>[];
                    packageTickets: PackageTicket<BackendType>[];
                }) => {
                    const tickets = response.tickets.map(mapProductToFrontend);
                    const packages = (response.packageTickets ?? []).map(
                        mapPackageTicketToFrontend
                    );
                    return [...tickets, ...packages];
                },
                providesTags: ['liftOperator'],
            }),
        }),
    });

// Skips by default while no lift operator is resolvable (logged-out session,
// B2B user) — a bare id would otherwise request `/liftoperators/undefined`.
export const useGetLiftOperatorQuery: typeof liftOperatorApi.useGetLiftOperatorQuery =
    (params, options) =>
        liftOperatorApi.useGetLiftOperatorQuery(params, {
            ...options,
            skip:
                options?.skip ||
                typeof params !== 'object' ||
                !params?.liftOperatorId,
        });

export const {
    useGetLiftOperatorsQuery,
    useGetLiftOperatorProductsQuery,
    useLazyGetAggregatedTicketsQuery,
    useLazyGetProductDetailsQuery,
    useGetStatisticsQuery,
    usePutLiftOperatorMutation,
} = liftOperatorApi;
